// Loans Management page — PRD_LoansManagement (C4, C6, C7, C12, C14).
//
// One row per non-voided loan (C13's single-field fetch), joined to the
// employee roster for the name / department / inactive badge (C7). Modelled on
// WorkSchedulePage.js (page frame, controls bar, table, footer per
// DESIGN-STANDARDS.md) and AdjustmentsPage.js (modal skeletons, users map).
// The modals, the history table, the badge and the row actions are exported for
// the tests; this page is the ONLY loans surface (C12 — the Employee Detail
// Loans tab was dropped at /test-impl 2026-09-26).
//
// Page load: the loans query and the employees query are both fatal (a row
// without its roster is meaningless). The pending-run read is not — see C16.

import React, { useState, useEffect, useCallback, useMemo, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase/config";
import { useAuth } from "../../hooks/useAuth";
import { useToast } from "../../contexts/ToastContext";
import Icon from "../icons/Icon";
import Modal from "../modals/Modal";
import EmployeePicker, { inactiveBadge } from "../EmployeePicker";
import { formatPeso as peso } from "../../utils/currency";
import { formatFilteredCount } from "../../utils/footerStats";
import { isEmployeeActiveToday } from "../../utils/employeeFilters";
import { matchesEmployeeSearch } from "../../utils/employeeSearch";
import { todayManilaYMD } from "../../utils/periodDate";
import { expandableRowProps } from "../../utils/expandableRow";
import {
  LOAN_STATUS,
  loanRemaining,
  loanStatus,
  paymentsWithBalance,
  num,
} from "../../utils/loanMath";
import {
  validateLoan,
  createLoan,
  updateLoan,
  setLoanPaused,
  closeLoan,
  voidLoan,
  recordLoanCorrection,
  fetchCompanyLoans,
  loanIdsInPendingRuns,
  DELETE_BLOCKED,
  PENDING_RUN_LOCKED,
  PENDING_UNKNOWN,
  PENDING_UNKNOWN_EDIT,
  TOTAL_BELOW_COLLECTED,
} from "../../services/loanService";

// ── C14 — the pinned copy ──────────────────────────────────────────────────────
// (c) — lives with the shared picker now (PRD_SharedEmployeePicker C6).
export { INACTIVE_BADGE } from "../EmployeePicker";
export const PAUSED_BADGE = "Paused"; // (i)
export const PAUSE_ACTION = "Pause deductions"; // (i)
// (r) — Void renamed Delete at /test-impl 2026-09-26: it is a soft delete, and
// "void" is accounting jargon. What it does is unchanged, including the hard
// block once any payment exists (C7) — that loan is Closed, never deleted.
export const DELETE_ACTION = "Delete";
export const RESUME_ACTION = "Resume deductions"; // (i)
export const REMAINING_LABEL = "Remaining to deduct"; // (g)
export const TOTAL_LABEL = "Loan Amount (₱)"; // (p) — (f)'s label and helper were withdrawn
// (p), (q) — /test-impl 2026-09-26. "Amortization" is loan jargon that means
// nothing for a cash advance, and the amount HR keys is simply what will be
// deducted, so the labels say that and nothing more (founder: "i want this simple").
// NB the SSS statement's own "Loan Amount" is the PRINCIPAL (₱26,000), while the
// figure this field wants is the Amortization total (₱28,794.48) — the founder
// accepted that risk with the helper removed.
export const MONTHLY_LABEL = "Monthly deduction (₱)";
export const PAID_BEFORE_LABEL = "Already deducted (₱)";
// (e)
export const PAID_BEFORE_HELPER =
  "Already deducted before this loan was entered here.";
// (o)
export const PAID_LOCK_HELPER = "Payments have been recorded, so this can't change. The Loan Amount can only be raised.";
// (u) C16 — the in-flight lock's helper, under every frozen money field.
export const PENDING_LOCK_HELPER =
  "A payroll run awaiting approval includes this loan, so the amounts can't change. Approve or reject that run first. The name and notes are still editable.";
// (v) C16 — the same freeze when the pending-run check could not run at all.
// It must NOT claim a run is pending: that is not known either way, and this
// account has no way to find out (/impl-review round 2, 2026-09-27).
export const PENDING_UNKNOWN_HELPER = PENDING_UNKNOWN_EDIT;

const LOAD_FAILED = "Couldn't load loans — please retry.";
const EMPTY_NONE = "No loans yet.";
const EMPTY_FILTERED = "No loans match.";
const NO_PAYMENTS = "No payments yet.";
// Firestore's message without its own trailing full stop (the WorkSchedulePage convention).
const reasonOf = (err) => (err?.message || "unknown error").replace(/\.$/, "");

const STATUS_FILTERS = [
  ["all", "All"], // the default (C12a), so it leads
  [LOAN_STATUS.ACTIVE, "Active"],
  [LOAN_STATUS.PAUSED, "Paused"],
  [LOAN_STATUS.CLOSED, "Closed"],
];

const inputStyle = {
  padding: "0.5rem 0.75rem",
  border: "1px solid var(--ui-border)",
  borderRadius: "6px",
  fontSize: "0.8125rem",
  fontFamily: "inherit",
};
const labelStyle = { fontSize: "0.75rem", fontWeight: 600, color: "var(--ui-text-secondary)", marginBottom: "0.25rem", display: "block" };
const helperStyle = { fontSize: "0.75rem", color: "var(--ui-text-muted)", margin: "-0.5rem 0 0.75rem 0" };
const fieldStyle = { display: "block", width: "100%", marginBottom: "0.75rem", ...inputStyle };
const modalBox = { background: "white", borderRadius: "12px", maxWidth: "560px", width: "100%", maxHeight: "90vh", display: "flex", flexDirection: "column", overflow: "hidden" };
const modalBody = { padding: "1.5rem", overflowY: "auto" };
const menuBox = {
  position: "fixed", zIndex: 1000, minWidth: "190px",
  background: "white", border: "1px solid var(--ui-border)", borderRadius: "8px",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.12)", overflow: "hidden", padding: "0.25rem",
};
const menuItem = {
  display: "block", width: "100%", textAlign: "left", padding: "0.5rem 0.75rem",
  border: "none", background: "transparent", cursor: "pointer",
  fontSize: "0.8125rem", fontFamily: "inherit", color: "var(--ui-text)", borderRadius: "6px",
};
const errorBand = { background: "var(--ui-danger-tint)", color: "var(--ui-danger-text)", borderRadius: "8px", padding: "0.625rem 0.875rem", fontSize: "0.8125rem", marginBottom: "0.75rem" };
const noteBand = { background: "var(--ui-primary-tint)", color: "var(--ui-primary-hover)", borderRadius: "8px", padding: "0.625rem 0.875rem", fontSize: "0.8125rem", marginBottom: "0.75rem" };
const primaryBtn = (disabled) => ({
  padding: "0.5rem 1rem",
  background: "var(--ui-primary)",
  color: "white",
  border: "none",
  borderRadius: "8px",
  fontWeight: 600,
  fontSize: "0.8125rem",
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.7 : 1,
});
const dangerBtn = (disabled) => ({ ...primaryBtn(disabled), background: "var(--ui-danger)" });
const smallBtn = {
  padding: "0.3rem 0.55rem",
  border: "1px solid var(--ui-border)",
  borderRadius: "6px",
  background: "white",
  cursor: "pointer",
  fontSize: "0.75rem",
  fontFamily: "inherit",
  whiteSpace: "nowrap",
};

const currentMonthKey = () => todayManilaYMD().slice(0, 7);

const initials = (name) =>
  (name || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();

// Project ONLY what the table and the picker need — never the raw doc (the
// adjustments C8 rule). activeToday is read off the raw doc before projection.
const projectEmployee = (d) => {
  const data = d.data();
  return {
    id: d.id,
    employeeId: data.employeeId,
    fullName:
      data.personalInfo?.fullName ||
      `${data.personalInfo?.firstName || ""} ${data.personalInfo?.lastName || ""}`.trim(),
    department: data.employment?.department || "",
    activeToday: isEmployeeActiveToday(data),
  };
};

// Title only — the × is the shared <Modal>'s (PRD_ModalBehavior C3).
const ModalHeader = ({ id, title }) => (
  <div style={{ marginBottom: "1rem", paddingRight: "2.5rem" }}>
    <h3 id={id} style={{ margin: 0, fontSize: "1rem", color: "var(--ui-text)" }}>{title}</h3>
  </div>
);

const ModalFooter = ({ onClose, saving, confirmLabel, onConfirm, disabled, danger, hideConfirm, closeLabel }) => (
  <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem", marginTop: "0.5rem" }}>
    <button type="button" onClick={onClose} disabled={saving} style={{ ...inputStyle, cursor: "pointer", background: "white" }}>
      {closeLabel || "Cancel"}
    </button>
    {!hideConfirm && (
      <button type="button" onClick={onConfirm} disabled={saving || disabled} style={danger ? dangerBtn(saving || disabled) : primaryBtn(saving || disabled)}>
        {saving ? "Saving…" : confirmLabel}
      </button>
    )}
  </div>
);

// ── Status badge (C7; DESIGN-STANDARDS §9) ────────────────────────────────────
const BADGE = {
  [LOAN_STATUS.ACTIVE]: { text: "Active", bg: "var(--ui-success-tint)", fg: "var(--ui-success-text)" },
  [LOAN_STATUS.PAUSED]: { text: PAUSED_BADGE, bg: "var(--ui-warning-tint)", fg: "var(--ui-warning-text)" },
  [LOAN_STATUS.CLOSED]: { text: "Closed", bg: "var(--ui-surface-recessed)", fg: "var(--ui-text-muted)" },
};
export const LoanStatusBadge = ({ status }) => {
  // No fallback to Active: a status with no badge (voided, which the page's own
  // query never returns) must render nothing rather than claim the loan is live
  // (/impl-review r3, 2026-09-28).
  const b = BADGE[status];
  if (!b) return null;
  return (
    <span style={{ background: b.bg, color: b.fg, padding: "0.25rem 0.75rem", borderRadius: "20px", fontSize: "0.75rem", fontWeight: 600, whiteSpace: "nowrap" }}>
      {b.text}
    </span>
  );
};

// The loans table and the payment-history panel share ONE column grid, both with
// table-layout: fixed. Without it each table sizes its own columns from its own
// content and the panel's edges land a few dozen pixels off the row it expands
// from, which reads as broken (/test-impl 2026-09-26). The two tables carry
// different data, so the widths suit both: a name / run id, a loan name / period,
// then four narrower columns.
const LOAN_COLS = ["26%", "22%", "13%", "15%", "12%", "12%"];
// The history has five columns to the table's six, so it declares its own grid;
// what matters is each header sitting over its own data, not matching the row
// above (founder, /test-impl 2026-09-26 — the By column went with it, since the
// question HR asks is which pay run took the money).
const HISTORY_COLS = ["26%", "26%", "14%", "17%", "17%"];
const LOAN_TABLE = { width: "100%", minWidth: "880px", borderCollapse: "collapse", tableLayout: "fixed" };
const LoanColGroup = () => (
  <colgroup>
    {LOAN_COLS.map((w, i) => (
      <col key={i} style={{ width: w }} />
    ))}
  </colgroup>
);

// ── Payment history (C5/C12) ───────────────────────────────────────────────────
export const LoanHistory = ({ loan }) => {
  const rows = paymentsWithBalance(loan);
  const cell = { padding: "10px 16px", fontSize: "0.8125rem", color: "var(--ui-text)", borderBottom: "1px solid var(--ui-surface-recessed)", overflow: "hidden", textOverflow: "ellipsis" };
  // textAlign left is explicit: a <th> centres by default, which left every header
  // sitting off its own column (/test-impl 2026-09-26).
  const head = { ...cell, fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--ui-text-muted)", fontWeight: 600, textAlign: "left" };
  const money = { ...cell, fontVariantNumeric: "tabular-nums", textAlign: "right", whiteSpace: "nowrap" };
  if (rows.length === 0) {
    return <div style={{ padding: "0.75rem 1rem", fontSize: "0.8125rem", color: "var(--ui-text-muted)" }}>{NO_PAYMENTS}</div>;
  }
  // Five columns at 10px/16px against the parent row's 12px/16px, so the panel
  // reads as a continuation of it rather than a ragged sub-table
  // (/test-impl 2026-09-26). Pay run leads: it is how HR ties a deduction back
  // to the run that took it, and a correction has no run, which is the
  // distinction the first column now carries.
  return (
    <table style={LOAN_TABLE} aria-label={`Payment history — ${loan.name}`}>
      <colgroup>
        {HISTORY_COLS.map((w, i) => (
          <col key={i} style={{ width: w }} />
        ))}
      </colgroup>
      <thead>
        <tr>
          <th style={head}>Pay run</th>
          <th style={head}>Pay period</th>
          <th style={head}>Month</th>
          <th style={{ ...head, textAlign: "right" }}>Amount</th>
          <th style={{ ...head, textAlign: "right" }}>Balance after</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((p, i) => (
          <tr key={`${p.paidAt || ""}-${i}`}>
            <td style={cell}>
              {p.runId || "Correction"}
              {!p.runId && p.reason && (
                <div style={{ fontSize: "0.75rem", color: "var(--ui-text-muted)" }}>{p.reason}</div>
              )}
            </td>
            <td style={{ ...cell, fontVariantNumeric: "tabular-nums" }}>
              {p.runId ? `${p.periodStart || "—"} – ${p.periodEnd || "—"}` : "—"}
            </td>
            <td style={{ ...cell, fontVariantNumeric: "tabular-nums" }}>{p.monthKey || "—"}</td>
            <td style={money}>{peso(p.amount)}</td>
            <td style={money}>{peso(p.balanceAfter)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

// ── Add / edit modal (C4, C6 paid-lock, C14 e/f/j/o) ─────────────────────────
const formFromLoan = (loan, lockedEmployee) => ({
  employeeId: loan?.employeeId || lockedEmployee?.employeeId || "",
  name: loan?.name || "",
  totalPayable: loan?.totalPayable != null ? String(loan.totalPayable) : "",
  monthlyAmortization: loan?.monthlyAmortization != null ? String(loan.monthlyAmortization) : "",
  firstDeductionMonth: loan?.firstDeductionMonth || currentMonthKey(),
  paidBefore: loan?.paidBefore != null ? String(loan.paidBefore) : "0",
  notes: loan?.notes || "",
});

const toNumber = (s) => (s === "" || s === null || s === undefined ? NaN : parseFloat(s));

export const LoanFormModal = ({ mode, loan, employees, lockedEmployee, pendingLocked, saving, onClose, onSave }) => {
  const isEdit = mode === "edit";
  const [form, setForm] = useState(() => formFromLoan(loan, lockedEmployee));
  const [error, setError] = useState(null);
  const titleId = useId();
  const paid = isEdit && num(loan?.paidTotal) > 0; // C6 paid-lock
  const inFlight = isEdit && Boolean(pendingLocked); // C16 — pendingLocked IS the reason string
  const floor = paid ? num(loan.paidBefore) + num(loan.paidTotal) : null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = () => {
    setError(null);
    const data = {
      employeeId: form.employeeId,
      name: form.name,
      totalPayable: toNumber(form.totalPayable),
      monthlyAmortization: toNumber(form.monthlyAmortization),
      firstDeductionMonth: form.firstDeductionMonth,
      paidBefore: form.paidBefore === "" ? 0 : toNumber(form.paidBefore),
      notes: form.notes,
    };
    const problem = validateLoan(data);
    if (problem) return setError(problem);
    if (paid && data.totalPayable < floor) return setError(TOTAL_BELOW_COLLECTED);
    onSave(data);
  };

  // C12: one flat list of ALL non-deleted employees, no date and no tenure
  // filter; the inactive badge is the only signal. The picker itself is the
  // shared one (PRD_SharedEmployeePicker C1). With only an ID — a soft-deleted
  // employee's loan — the locked state reads "Employee EMP-####".
  const pickerLocked = isEdit || Boolean(lockedEmployee);
  const chosen =
    (employees || []).find((e) => e.employeeId === form.employeeId) ??
    lockedEmployee ??
    (form.employeeId ? { employeeId: form.employeeId } : null);

  return (
    <Modal isOpen onClose={onClose} busy={saving} labelledBy={titleId} style={modalBox}>
      <div style={modalBody}>
        <ModalHeader id={titleId} title={isEdit ? `Edit loan — ${loan?.name || ""}` : "Add loan"} />

        <span style={labelStyle}>Employee</span>
        <EmployeePicker
          employees={employees}
          value={chosen}
          onChange={(row) => set("employeeId", row ? row.employeeId : "")}
          locked={pickerLocked}
        />

        <label style={labelStyle}>
          Loan name
          <input
            type="text"
            placeholder="e.g. SSS Salary Loan, Pag-IBIG Multi-Purpose Loan, Cash Advance"
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            style={{ ...fieldStyle, marginTop: "0.25rem" }}
          />
        </label>

        {inFlight && <div style={noteBand}>{pendingLocked}</div>}

        <label style={labelStyle}>
          {TOTAL_LABEL}
          <input
            type="number"
            step="0.01"
            min={floor ?? 0}
            value={form.totalPayable}
            disabled={inFlight}
            onChange={(e) => set("totalPayable", e.target.value)}
            style={{ ...fieldStyle, marginTop: "0.25rem", background: inFlight ? "var(--ui-surface-recessed)" : "white" }}
          />
        </label>

        <div style={{ display: "flex", gap: "0.75rem" }}>
          <label style={{ ...labelStyle, flex: 1 }}>
            {MONTHLY_LABEL}
            <input type="number" step="0.01" min="0" value={form.monthlyAmortization} disabled={inFlight} onChange={(e) => set("monthlyAmortization", e.target.value)} style={{ ...fieldStyle, marginTop: "0.25rem", background: inFlight ? "var(--ui-surface-recessed)" : "white" }} />
          </label>
          <label style={{ ...labelStyle, flex: 1 }}>
            First deduction month
            <input type="month" value={form.firstDeductionMonth} disabled={inFlight} onChange={(e) => set("firstDeductionMonth", e.target.value)} style={{ ...fieldStyle, marginTop: "0.25rem", background: inFlight ? "var(--ui-surface-recessed)" : "white" }} />
          </label>
        </div>

        <label style={labelStyle}>
          {PAID_BEFORE_LABEL}
          <input
            type="number"
            step="0.01"
            min="0"
            value={form.paidBefore}
            disabled={paid || inFlight}
            onChange={(e) => set("paidBefore", e.target.value)}
            style={{ ...fieldStyle, marginTop: "0.25rem", background: (paid || inFlight) ? "var(--ui-surface-recessed)" : "white" }}
          />
        </label>
        <p style={helperStyle}>{paid ? PAID_LOCK_HELPER : PAID_BEFORE_HELPER}</p>

        <label style={labelStyle}>
          Notes (optional)
          <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} style={{ ...fieldStyle, marginTop: "0.25rem", resize: "vertical" }} />
        </label>

        {error && <div style={errorBand}>{error}</div>}
        <ModalFooter onClose={onClose} saving={saving} confirmLabel={isEdit ? "Save Changes" : "Add Loan"} onConfirm={submit} />
      </div>
    </Modal>
  );
};

// ── Pause / Resume (C7) — confirmed, like every other lifecycle action ────────
export const PauseLoanModal = ({ loan, saving, onClose, onConfirm }) => {
  const titleId = useId();
  const resuming = Boolean(loan.paused);
  return (
    <Modal isOpen onClose={onClose} busy={saving} labelledBy={titleId} style={modalBox}>
      <div style={modalBody}>
        <ModalHeader id={titleId} title={`${resuming ? "Resume" : "Pause"} deductions — ${loan.name}`} />
        <p style={{ fontSize: "0.8125rem", color: "var(--ui-text-secondary)", marginTop: 0 }}>
          {resuming
            ? `The loan comes back into payroll runs you start from now on. Nothing about the balance changes — ${REMAINING_LABEL} is ${peso(loanRemaining(loan))}.`
            : `The loan stops appearing in payroll runs you start from now on. It does NOT affect a run already awaiting approval — that run still deducts and credits it. The balance and the payment history are untouched: ${REMAINING_LABEL} stays at ${peso(loanRemaining(loan))}. Resume it any time.`}
        </p>
        <ModalFooter onClose={onClose} saving={saving} confirmLabel={resuming ? RESUME_ACTION : PAUSE_ACTION} onConfirm={onConfirm} />
      </div>
    </Modal>
  );
};

// ── Close (C7) ─────────────────────────────────────────────────────────────────
export const CloseLoanModal = ({ loan, saving, onClose, onConfirm }) => {
  const [reason, setReason] = useState("");
  const titleId = useId();
  return (
    <Modal isOpen onClose={onClose} busy={saving} labelledBy={titleId} style={modalBox}>
      <div style={modalBody}>
        <ModalHeader id={titleId} title={`Close loan — ${loan.name}`} />
        <p style={{ fontSize: "0.8125rem", color: "var(--ui-text-secondary)", marginTop: 0 }}>
          Closing stops all further deductions. Use it for a loan settled outside payroll — paid directly to the agency, taken from final pay, or folded into a renewal. {REMAINING_LABEL} is {peso(loanRemaining(loan))}. This can't be undone.
        </p>
        <label style={labelStyle}>
          Reason (optional)
          <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} style={{ ...fieldStyle, marginTop: "0.25rem" }} />
        </label>
        <ModalFooter onClose={onClose} saving={saving} confirmLabel="Close Loan" onConfirm={() => onConfirm(reason)} />
      </div>
    </Modal>
  );
};

// ── Void (C7) — only ever opened for an unpaid loan ─────────────────────────────
export const DeleteLoanModal = ({ loan, blockedReason, saving, onClose, onConfirm }) => {
  const titleId = useId();
  return (
    <Modal isOpen onClose={onClose} busy={saving} labelledBy={titleId} style={modalBox}>
      <div style={modalBody}>
        <ModalHeader id={titleId} title={`Delete loan — ${loan.name}`} />
        {blockedReason ? (
          <div style={errorBand}>{blockedReason}</div>
        ) : (
          <p style={{ fontSize: "0.8125rem", color: "var(--ui-text-secondary)", marginTop: 0 }}>
            Delete a loan that was entered by mistake. Payroll has not paid against it. It disappears from Loans Management and can't be undone.
          </p>
        )}
        <ModalFooter
          onClose={onClose}
          saving={saving}
          confirmLabel="Delete Loan"
          onConfirm={onConfirm}
          danger
          hideConfirm={Boolean(blockedReason)}
          closeLabel={blockedReason ? "OK" : "Cancel"}
        />
      </div>
    </Modal>
  );
};

// ── Record a correction (C5; payroll:approve only) ─────────────────────────────
export const CorrectionModal = ({ loan, saving, onClose, onSave }) => {
  const [amount, setAmount] = useState("");
  const [monthKey, setMonthKey] = useState(currentMonthKey());
  const [reason, setReason] = useState("");
  const [error, setError] = useState(null);
  const titleId = useId();

  const submit = () => {
    setError(null);
    const a = toNumber(amount);
    if (Number.isNaN(a) || a === 0) return setError("Enter a non-zero amount.");
    if (!reason.trim()) return setError("A reason is required.");
    onSave({ amount: a, monthKey, reason: reason.trim() });
  };

  return (
    <Modal isOpen onClose={onClose} busy={saving} labelledBy={titleId} style={modalBox}>
      <div style={modalBody}>
        <ModalHeader id={titleId} title={`Record a correction — ${loan.name}`} />
        <div style={noteBand}>
          Adjusts this loan's ledger only — it never changes the employee's pay. A positive amount records money that was deducted but not credited; a negative amount gives the balance back. To refund the employee, add an earning adjustment in the next payroll run.
        </div>
        <label style={labelStyle}>
          Amount (negative gives the balance back)
          <input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} style={{ ...fieldStyle, marginTop: "0.25rem" }} />
        </label>
        <label style={labelStyle}>
          Month it applies to
          <input type="month" value={monthKey} onChange={(e) => setMonthKey(e.target.value)} style={{ ...fieldStyle, marginTop: "0.25rem" }} />
        </label>
        <label style={labelStyle}>
          Reason
          <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} style={{ ...fieldStyle, marginTop: "0.25rem" }} />
        </label>
        {error && <div style={errorBand}>{error}</div>}
        <ModalFooter onClose={onClose} saving={saving} confirmLabel="Record Correction" onConfirm={submit} />
      </div>
    </Modal>
  );
};

// ── Row actions — a kebab menu, the PayrollList pattern (founder, /test-impl
// 2026-09-26). History is NOT here: clicking the row expands the payment
// history, as the payroll wizard's expandable rows do. Every item stops
// propagation so opening the menu never toggles the row underneath.
export const LoanActions = ({ row, canEdit, canCorrect, onEdit, onTogglePause, onClose, onDelete, onCorrect }) => {
  const [open, setOpen] = useState(false);
  const [at, setAt] = useState(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const live = row.status === LOAN_STATUS.ACTIVE || row.status === LOAN_STATUS.PAUSED;

  // The menu is PORTALLED to <body> with fixed coordinates. The table's card sets
  // overflow-x:auto + overflow-y:hidden for the horizontal scroll, which makes it a
  // clipping box: an absolutely-positioned menu on the last row was cut off at the
  // footer (/test-impl 2026-09-26). Per CSS, a `visible` on one axis computes to
  // `auto` when the other is not visible, so the overflow cannot simply be relaxed.
  // Flipping upward does not help either — with one row the card is shorter than the
  // menu in both directions. PayrollList's Actions menu is portalled for the same
  // reason (PayrollList.js placeMenu) — but it keeps its CSS class, so its upward
  // branch must pass top:'auto' rather than undefined (/impl-review r2).
  const place = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const H = 230; // generous; only decides which side to open
    const below = window.innerHeight - r.bottom;
    setAt({ top: below < H ? undefined : r.bottom + 4, bottom: below < H ? window.innerHeight - r.top + 4 : undefined, right: window.innerWidth - r.right });
  };

  useEffect(() => {
    if (!open) return undefined;
    const outside = (e) =>
      !btnRef.current?.contains(e.target) && !menuRef.current?.contains(e.target) && setOpen(false);
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    const shut = () => setOpen(false);
    document.addEventListener("mousedown", outside);
    document.addEventListener("keydown", onEsc);
    window.addEventListener("resize", shut);
    // capture:true so a scroll inside the table's own scroller closes it too
    window.addEventListener("scroll", shut, true);
    return () => {
      document.removeEventListener("mousedown", outside);
      document.removeEventListener("keydown", onEsc);
      window.removeEventListener("resize", shut);
      window.removeEventListener("scroll", shut, true);
    };
  }, [open]);

  const item = (label, onPick, danger) => (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); setOpen(false); onPick(row); }}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") e.stopPropagation(); }}
      style={{ ...menuItem, ...(danger ? { color: "var(--ui-danger)" } : null) }}
    >
      {label}
    </button>
  );

  if (!canEdit && !canCorrect) return null;
  return (
    <div style={{ display: "inline-block" }}>
      <button
        ref={btnRef}
        type="button"
        onClick={(e) => { e.stopPropagation(); if (!open) place(); setOpen((v) => !v); }}
        // The row's own Enter/Space handler calls preventDefault (to stop Space
        // scrolling), which cancels this button's activation — so without this the
        // menu has no keyboard route at all and Enter just expands the history
        // (/impl-review r2, 2026-09-27).
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") e.stopPropagation(); }}
        aria-label={`Actions for ${row.name}`}
        aria-expanded={open}
        aria-haspopup="menu"
        style={{ ...smallBtn, padding: "0.3rem 0.4rem", lineHeight: 0 }}
      >
        <Icon name="more-vertical" size={16} />
      </button>
      {open && at && createPortal(
        <div ref={menuRef} role="menu" onClick={(e) => e.stopPropagation()} style={{ ...menuBox, ...at }}>
          {canEdit && item("Edit", onEdit)}
          {canEdit && live && item(row.paused ? RESUME_ACTION : PAUSE_ACTION, onTogglePause)}
          {canEdit && live && item("Close", onClose)}
          {canCorrect && item("Record a correction", onCorrect)}
          {canEdit && item(DELETE_ACTION, onDelete, true)}
        </div>,
        document.body,
      )}
    </div>
  );
};

// Sort icon + sortable header — the WorkSchedulePage / AdjustmentsPage pattern.
const SortIcon = ({ field, sortField, sortDirection }) => {
  const isActive = sortField === field;
  const base = { marginLeft: "4px", verticalAlign: "middle" };
  if (isActive && sortDirection === "asc") return <Icon name="arrow-up" size={12} style={base} />;
  if (isActive && sortDirection === "desc") return <Icon name="arrow-down" size={12} style={base} />;
  return <Icon name="chevrons-up-down" size={12} style={{ ...base, opacity: 0.5 }} />;
};
const SortableTh = ({ field, label, style, sortField, sortDirection, onSort }) => {
  const [hover, setHover] = useState(false);
  return (
    <th
      onClick={() => onSort(field)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ ...style, cursor: "pointer", background: hover ? "rgba(255,255,255,0.1)" : undefined }}
    >
      {label}
      <SortIcon field={field} sortField={sortField} sortDirection={sortDirection} />
    </th>
  );
};

// Join a loan to its roster entry and derive the display fields once.
export const toRow = (loan, employeeById) => ({
  ...loan,
  employee: employeeById.get(String(loan.employeeId)) || null,
  status: loanStatus(loan),
  remaining: loanRemaining(loan),
});

// ── Page ───────────────────────────────────────────────────────────────────────
const LoansPage = () => {
  const { user, company, hasPermission } = useAuth();
  const canEdit = hasPermission("loan:edit");
  const canCorrect = hasPermission("payroll:approve");
  const toast = useToast();
  const userDocId = user?.userData?.userDocId || user?.uid;

  const [loading, setLoading] = useState(true);
  const [loans, setLoans] = useState([]);
  const [employees, setEmployees] = useState([]);

  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [sortField, setSortField] = useState("employee");
  const [sortDirection, setSortDirection] = useState("asc");

  const [formModal, setFormModal] = useState(null); // { mode, loan }
  const [pendingLoanIds, setPendingLoanIds] = useState(new Set()); // C16
  const [pendingUnknown, setPendingUnknown] = useState(false); // C16 — payroll runs unreadable
  const [pauseTarget, setPauseTarget] = useState(null);
  const [closeTarget, setCloseTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [correctionTarget, setCorrectionTarget] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());
  const [saving, setSaving] = useState(false);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // ── Data load ─────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    if (!company?.id) return;
    setLoading(true);
    try {
      const [loanRows, empSnap, pendingIds] = await Promise.all([
        fetchCompanyLoans(company.id),
        getDocs(query(collection(db, "companies", company.id, "employees"), where("role", "==", "employee"))),
        // C16 — the lock reads payrollRuns, which needs payroll:view. A loans
        // clerk has no such key, so skip it entirely when there is nothing to
        // lock, and treat a failure as "unknown" — which freezes the money
        // fields and refuses delete, saying which access is missing rather than
        // claiming a run is pending (/test-impl 2026-09-26, /impl-review r2).
        canEdit
          ? loanIdsInPendingRuns(company.id).catch((err) => {
            // permission-denied is the NORMAL case here, not a fault: a loans
            // clerk holds loan:edit and no payroll:view, and payrollRuns needs
            // payroll:view. Only an unexpected failure is worth the console.
            // (`permission-denied` per @firebase/firestore 4.8.0.)
            if (err?.code !== "permission-denied") {
              console.error("[LoansPage] pending-run read failed — delete and the money fields are blocked:", err);
            }
            return null;
          })
          : Promise.resolve(new Set()),
      ]);
      setEmployees(
        empSnap.docs
          .filter((d) => !d.data().metadata?.isDeleted)
          .map(projectEmployee)
          .filter((e) => e.employeeId),
      );
      setLoans(loanRows);
      setPendingLoanIds(pendingIds || new Set());
      setPendingUnknown(pendingIds === null);
    } catch (err) {
      console.error("[LoansPage] load failed:", err);
      toast.error(LOAD_FAILED);
      setLoans([]);
    } finally {
      setLoading(false);
    }
  }, [company?.id, canEdit, toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ── Derived view ──────────────────────────────────────────────────────────
  const employeeById = useMemo(() => new Map(employees.map((e) => [String(e.employeeId), e])), [employees]);
  const rows = useMemo(() => loans.map((l) => toRow(l, employeeById)), [loans, employeeById]);
  const departments = useMemo(
    () => [...new Set(employees.map((e) => e.department).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [employees],
  );

  // Status, search and department are all FILTERS (C12a) — this just applies the
  // first of them before visibleRows applies the other two.
  const scopeRows = useMemo(
    () => rows.filter((r) => statusFilter === "all" || r.status === statusFilter),
    [rows, statusFilter],
  );
  const visibleRows = useMemo(() => {
    const filtered = scopeRows.filter((r) => {
      if (departmentFilter && (r.employee?.department || "") !== departmentFilter) return false;
      // r.employee is null for a soft-deleted employee's loan, so the row's own
      // ID rides as an extra term beside the loan name (C3).
      return matchesEmployeeSearch(r.employee, searchTerm, [r.employeeId, r.name]);
    });
    const name = (r) => r.employee?.fullName || `EMP-${r.employeeId}`;
    return filtered.sort((a, b) => {
      let c;
      if (sortField === "remaining") c = a.remaining - b.remaining;
      else if (sortField === "loan") c = a.name.localeCompare(b.name);
      else c = name(a).localeCompare(name(b)) || a.name.localeCompare(b.name);
      return sortDirection === "asc" ? c : -c;
    });
  }, [scopeRows, searchTerm, departmentFilter, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Status is a FILTER here, not a scope selector — the same shape AdjustmentsPage
  // uses, so every figure in the footer follows every control above it
  // (DESIGN-STANDARDS, Filter-Aware Stats).
  const filterActive =
    Boolean(searchTerm) || Boolean(departmentFilter) || statusFilter !== "all";
  const countDisplay = formatFilteredCount(visibleRows.length, rows.length, filterActive);
  // All three recompute over the filtered rows (DESIGN-STANDARDS). This only
  // works because status is a filter defaulting to "all": when it defaulted to
  // Active, Paused could only ever read 0.
  const stats = useMemo(() => ({
    active: visibleRows.filter((r) => r.status === LOAN_STATUS.ACTIVE).length,
    paused: visibleRows.filter((r) => r.status === LOAN_STATUS.PAUSED).length,
    remaining: visibleRows.reduce((s, r) => s + r.remaining, 0),
  }), [visibleRows]);

  const toggleHistory = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // ── Writes ────────────────────────────────────────────────────────────────
  const run = async (action, successMessage, closeModal) => {
    setSaving(true);
    try {
      await action();
      toast.success(successMessage);
      closeModal();
      await fetchData();
    } catch (err) {
      toast.error(err?.message ? `${reasonOf(err)}.` : "Something went wrong — please retry.");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveForm = (data) =>
    formModal.mode === "edit"
      ? run(() => updateLoan(company.id, formModal.loan.id, data, userDocId), "Loan updated.", () => setFormModal(null))
      : run(() => createLoan(company.id, data, userDocId), "Loan added.", () => setFormModal(null));

  const handleTogglePause = () =>
    run(
      () => setLoanPaused(company.id, pauseTarget.id, !pauseTarget.paused, userDocId),
      pauseTarget.paused ? "Deductions resumed." : "Deductions paused.",
      () => setPauseTarget(null),
    );

  const handleClose = (reason) =>
    run(() => closeLoan(company.id, closeTarget.id, reason, userDocId), "Loan closed.", () => setCloseTarget(null));

  // C7/C14d + C16: the delete modal always opens and carries the reason itself when
  // there is one — a corner toast was too easy to miss (founder, /test-impl 2026-09-26).
  // C16 — an unreadable pending-run check freezes the money fields exactly as a
  // known pending run does; only the sentence differs (/impl-review r2).
  const pendingLockReason = (loan) => {
    if (!loan) return null;
    if (pendingUnknown) return PENDING_UNKNOWN_HELPER;
    return pendingLoanIds.has(loan.id) ? PENDING_LOCK_HELPER : null;
  };

  const deleteBlockedReason = !deleteTarget
    ? null
    : num(deleteTarget.paidTotal) > 0
      ? DELETE_BLOCKED
      : pendingUnknown
        ? PENDING_UNKNOWN
        : pendingLoanIds.has(deleteTarget.id)
          ? PENDING_RUN_LOCKED
          : null;
  const handleDeleteConfirm = () =>
    run(() => voidLoan(company.id, deleteTarget.id, userDocId), "Loan deleted.", () => setDeleteTarget(null));

  const handleCorrection = (payload) =>
    run(
      () => recordLoanCorrection(company.id, correctionTarget.id, payload, userDocId),
      "Correction recorded.",
      () => setCorrectionTarget(null),
    );

  // ── Render ────────────────────────────────────────────────────────────────
  const th = {
    padding: "14px 16px",
    textAlign: "left",
    fontWeight: "600",
    fontSize: "12px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: "white",
    userSelect: "none",
    whiteSpace: "nowrap",
  };
  const td = { padding: "12px 16px", fontSize: "0.875rem", fontWeight: "500", color: "var(--ui-text)" };
  const money = { ...td, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };

  const renderRow = (row) => {
    const emp = row.employee;
    const isOpen = expanded.has(row.id);
    return (
      <React.Fragment key={row.id}>
        <tr
          style={{ borderBottom: "1px solid var(--ui-surface-recessed)", cursor: "pointer" }}
          {...expandableRowProps(isOpen, () => toggleHistory(row.id))}
          aria-label={`Payment history for ${row.name}`}
        >
          <td style={td}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px", minWidth: 0 }}>
              <div style={{ width: "36px", height: "36px", borderRadius: "50%", background: "var(--ui-primary-tint)", color: "var(--ui-primary)", fontSize: "13px", fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                {initials(emp?.fullName)}
              </div>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", minWidth: 0 }}>
                  <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {emp?.fullName || "Unknown employee"}
                  </span>
                  {emp && !emp.activeToday && inactiveBadge}
                </div>
                <div style={{ fontSize: "0.75rem", color: "var(--ui-text-muted)", fontVariantNumeric: "tabular-nums" }}>
                  EMP-{row.employeeId}{emp?.department ? ` · ${emp.department}` : ""}
                </div>
              </div>
            </div>
          </td>
          <td style={td}>
            <div>{row.name}</div>
          </td>
          <td style={money}>{peso(row.monthlyAmortization)}</td>
          <td style={money}>{peso(row.remaining)}</td>
          <td style={td}><LoanStatusBadge status={row.status} /></td>
          <td style={{ ...td, whiteSpace: "nowrap" }}>
            <LoanActions
              row={row}
              canEdit={canEdit}
              canCorrect={canCorrect}
              onEdit={(r) => setFormModal({ mode: "edit", loan: r })}
              onTogglePause={setPauseTarget}
              onClose={setCloseTarget}
              onDelete={setDeleteTarget}
              onCorrect={setCorrectionTarget}
            />
          </td>
        </tr>
        {isOpen && (
          <tr style={{ borderBottom: "1px solid var(--ui-surface-recessed)", background: "var(--ui-surface-recessed)" }}>
            <td colSpan={6} style={{ padding: "0 0 0.25rem 0" }}>
              <LoanHistory loan={row} />
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  const statCards = (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "1rem", marginBottom: "1.5rem" }}>
      {[
        ["Loans", countDisplay],
        [`${REMAINING_LABEL} (Shown)`, peso(stats.remaining)],
        ["Active", stats.active],
        ["Paused", stats.paused],
      ].map(([label, value]) => (
        <div key={label} style={{ position: "relative", background: "white", borderRadius: "8px", border: "1px solid var(--ui-border)", padding: "0.875rem 1rem", overflow: "hidden" }}>
          <div style={{ position: "absolute", left: 0, top: "15%", bottom: "15%", width: "4px", background: "var(--ui-primary)", borderRadius: "0 2px 2px 0" }} />
          <div style={{ fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--ui-text-muted)" }}>{label}</div>
          <div style={{ fontSize: "18px", fontWeight: 600, fontVariantNumeric: "tabular-nums", color: "var(--ui-text)" }}>{value}</div>
        </div>
      ))}
    </div>
  );

  return (
    <div style={{ fontFamily: "var(--font-family-primary)" }}>
      <div style={{ width: "100%" }}>
        <h1 style={{ fontSize: isMobile ? "1.125rem" : "1.25rem", fontWeight: "700", color: "var(--ui-primary)", margin: isMobile ? "0 0 1rem 0" : "0 0 0.75rem 0" }}>
          Loans Management
        </h1>

        {isMobile && !loading && statCards}

        {/* Controls bar */}
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "stretch" : "center",
            gap: "0.75rem",
            padding: isMobile ? "1rem" : "0.625rem 1rem",
            background: "white",
            borderRadius: "12px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            marginBottom: "0.75rem",
          }}
        >
          <input
            type="text"
            placeholder="Search employee or loan…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ ...inputStyle, flex: 1, minWidth: "160px" }}
          />
          <select value={departmentFilter} onChange={(e) => setDepartmentFilter(e.target.value)} aria-label="Department" style={{ ...inputStyle, minWidth: "140px" }}>
            <option value="">All departments</option>
            {departments.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} aria-label="Status" style={{ ...inputStyle, minWidth: "120px" }}>
            {STATUS_FILTERS.map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          {canEdit && (
            <>
              {!isMobile && <div style={{ width: "1px", height: "24px", background: "var(--ui-border)", flexShrink: 0 }} />}
              <button
                type="button"
                onClick={() => setFormModal({ mode: "add", loan: null })}
                style={{ ...primaryBtn(false), flexShrink: 0, whiteSpace: "nowrap", display: "flex", alignItems: "center", justifyContent: "center", gap: "0.375rem" }}
              >
                <Icon name="plus" size={14} /> Add Loan
              </button>
            </>
          )}
        </div>

        {/* Table */}
        <div style={{ background: "white", borderRadius: "12px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", overflowX: "auto", overflowY: "hidden" }}>
          {loading ? (
            <div style={{ padding: "3rem", textAlign: "center", color: "var(--ui-text-muted)" }}>Loading loans…</div>
          ) : visibleRows.length === 0 ? (
            <div style={{ padding: "3rem", textAlign: "center", color: "var(--ui-text-muted)" }}>
              {filterActive ? EMPTY_FILTERED : EMPTY_NONE}
            </div>
          ) : (
            <table style={LOAN_TABLE}>
              <LoanColGroup />
              <thead>
                <tr style={{ background: "var(--ui-gradient-primary)" }}>
                  <SortableTh field="employee" label="Employee" style={th} sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
                  <SortableTh field="loan" label="Loan" style={th} sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
                  <th style={th}>Monthly</th>
                  <SortableTh field="remaining" label={REMAINING_LABEL} style={th} sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
                  <th style={th}>Status</th>
                  <th style={th}>Actions</th>
                </tr>
              </thead>
              <tbody>{visibleRows.map(renderRow)}</tbody>
            </table>
          )}

          {/* Footer (desktop) */}
          {!isMobile && !loading && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "16px",
                padding: "16px 20px",
                background: "linear-gradient(135deg, var(--ui-bg) 0%, var(--ui-surface-recessed) 100%)",
                borderTop: "1px solid var(--ui-border)",
              }}
            >
              <div style={{ display: "flex", gap: "28px", flexWrap: "wrap" }}>
                <div>
                  <div data-testid="loans-footer-count" style={{ fontSize: "20px", fontWeight: "600", fontVariantNumeric: "tabular-nums", color: "var(--ui-primary)" }}>{countDisplay}</div>
                  <div style={{ fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--ui-text-muted)", whiteSpace: "nowrap" }}>Loans</div>
                </div>
                {[
                  ["remaining", `${REMAINING_LABEL} (Shown)`, peso(stats.remaining)],
                  ["active", "Active", stats.active],
                  ["paused", "Paused", stats.paused],
                ].map(([id, label, value]) => (
                  <div key={id}>
                    <div data-testid={`loans-stat-${id}`} style={{ fontSize: "16px", fontWeight: "600", fontVariantNumeric: "tabular-nums", color: "var(--ui-text)" }}>{value}</div>
                    <div style={{ fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--ui-text-muted)", whiteSpace: "nowrap" }}>{label}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {formModal && (
        <LoanFormModal
          mode={formModal.mode}
          loan={formModal.loan}
          pendingLocked={pendingLockReason(formModal.loan)}
          employees={employees}
          saving={saving}
          onClose={() => setFormModal(null)}
          onSave={handleSaveForm}
        />
      )}
      {pauseTarget && <PauseLoanModal loan={pauseTarget} saving={saving} onClose={() => setPauseTarget(null)} onConfirm={handleTogglePause} />}
      {closeTarget && <CloseLoanModal loan={closeTarget} saving={saving} onClose={() => setCloseTarget(null)} onConfirm={handleClose} />}
      {deleteTarget && <DeleteLoanModal loan={deleteTarget} blockedReason={deleteBlockedReason} saving={saving} onClose={() => setDeleteTarget(null)} onConfirm={handleDeleteConfirm} />}
      {correctionTarget && (
        <CorrectionModal loan={correctionTarget} saving={saving} onClose={() => setCorrectionTarget(null)} onSave={handleCorrection} />
      )}
    </div>
  );
};

export default LoansPage;
